import {Observer} from 'mobx-react';

import {i18n} from '@yourcoach/i18n';

import type {
  MembershipDeclineReason,
  MembershipDeclineReasonConfirmResult,
} from './types';

import {alert} from '../../../../components/alert';
import {Checkbox} from '../../../../uikit/Checkbox';
import {Text} from '../../../../uikit/Text';
import {logger} from '../../../../utils/logger';
import {createField, createTextInputField} from '../../../../utils/validation';
import {COMMAND} from '../../utils';
import * as S from './styles';
import {getReasonOptions, getReasonTranslations} from './utils';

const REASON_MAX_LENGTH = 255;

type Params = {
  isIndividualProgram: boolean;
};

export const membershipsDeclineRequestedReasonConfirm = ({
  isIndividualProgram,
}: Params) => {
  const options = getReasonOptions(isIndividualProgram);

  const customReasonInput = createTextInputField('customReason', {
    validationRule: {
      max: REASON_MAX_LENGTH,
      messages: {
        required: i18n.t('shared:reason_required_error'),
        stringMax: i18n.t('shared:reason_max_length_error', {
          maxLength: REASON_MAX_LENGTH,
        }),
      },
      type: 'string',
    },
  });

  const reasonField = createField<MembershipDeclineReason>('reason', {
    defaultValue: options[0]!.id,
    validationRule: {
      messages: {
        required: i18n.t('shared:message_required_error'),
      },
      type: 'string',
    },
  });

  const onConfirm = (
    resolve: (value: MembershipDeclineReasonConfirmResult) => void,
  ) => {
    const isOtherReason = reasonField.value === 'other';

    if (isOtherReason) {
      if (!customReasonInput.validate()) {
        return;
      }
    } else {
      if (!reasonField.validate()) {
        return;
      }
    }

    logger.event('request_decline_reason_tap');

    resolve({
      reason: isOtherReason
        ? customReasonInput.value.trim()
        : getReasonTranslations()[reasonField.value],
    });
  };

  const onCancel = (resolve: (value: typeof COMMAND.CANCEL) => void) =>
    resolve(COMMAND.CANCEL);

  return alert.show<
    MembershipDeclineReasonConfirmResult | typeof COMMAND.CANCEL
  >({
    beforeButtonsSlot: () => (
      <Observer>
        {() => (
          <S.ReasonContainer>
            <S.OptionsList>
              {options.map(({id, label}) => (
                <S.OptionContainer key={id}>
                  <Checkbox
                    checked={reasonField.value === id}
                    onClick={() => reasonField.setValue(id)}
                    variant="circle"
                  />
                  <Text>{label}</Text>
                </S.OptionContainer>
              ))}
            </S.OptionsList>
            {reasonField.value === 'other' && (
              <S.CustomReasonContainer>
                <Text>{i18n.t('shared:reason')}</Text>
                <S.CustomReasonInput
                  error={customReasonInput.error ?? ''}
                  maxLength={REASON_MAX_LENGTH}
                  onChange={customReasonInput.onChange}
                  placeholder={i18n.t(
                    'shared:provide_reason_why_request_was_declined',
                  )}
                  ref={customReasonInput.ref}
                  value={customReasonInput.value}
                />
              </S.CustomReasonContainer>
            )}
          </S.ReasonContainer>
        )}
      </Observer>
    ),
    buttons: [
      {
        label: i18n.t('shared:decline_requests'),
        onAwaitableClick: onConfirm,
        scheme: 'primary',
      },
      {
        label: i18n.t('shared:cancel'),
        onAwaitableClick: onCancel,
      },
    ],
    isDismissible: true,
    onAwaitableDismiss: onCancel,
    title: i18n.t('shared:decline_requests'),
  });
};
